import React, { useState, useEffect, useRef, useCallback } from "react";
import "./Villager.css";

interface VillagerProps {
  user?: any;
}

const GAME_DURATION = 120; // seconds (hidden from players in prod)
const WALK_SPEED = 0.5;
const CHARGE_SPEED = 0.8; // % per frame (fills in ~2 sec)
const STAMINA_DRAIN = 0.05; // % per frame (slower drain)
const STAMINA_BOOST = 25; // % boost when catching villager
const LOOK_STAMINA_COST = 10; // fixed chunk cost per look action

const Villager: React.FC<VillagerProps> = () => {
  // Game state
  const [gameStarted, setGameStarted] = useState(false);
  const [gameOver, setGameOver] = useState(false);
  const [winner, setWinner] = useState<"king" | "villager" | null>(null);
  const [timeLeft, setTimeLeft] = useState(GAME_DURATION);

  // Player states
  const [kingStamina, setKingStamina] = useState(100);
  const [villagerCharge, setVillagerCharge] = useState(0);
  const [isKingLooking, setIsKingLooking] = useState(false);
  const [isVillagerCharging, setIsVillagerCharging] = useState(false);
  const [villagerInPrison, setVillagerInPrison] = useState(false);
  const [prisonTimer, setPrisonTimer] = useState(0);

  // Position for walking animation
  const [walkPosition, setWalkPosition] = useState(0);

  // Score tracking
  const [kingWins, setKingWins] = useState(0);
  const [villagerWins, setVillagerWins] = useState(0);

  // Who is currently which role (for role swapping)
  const [player1Role, setPlayer1Role] = useState<"villager" | "king">("villager");

  const keysPressed = useRef<Set<string>>(new Set());
  const gameLoopRef = useRef<number | null>(null);
  const chargeRef = useRef(0);
  const wasLookingRef = useRef(false);
  const staminaRef = useRef(100);

  // Key handlers
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (!gameStarted || gameOver) return;
    keysPressed.current.add(e.code);
  }, [gameStarted, gameOver]);

  const handleKeyUp = useCallback((e: KeyboardEvent) => {
    keysPressed.current.delete(e.code);
  }, []);

  useEffect(() => {
    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("keyup", handleKeyUp);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("keyup", handleKeyUp);
    };
  }, [handleKeyDown, handleKeyUp]);

  // Game timer
  useEffect(() => {
    if (!gameStarted || gameOver) return;

    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          // Time's up - whoever is king wins
          setGameOver(true);
          // Player 1 wins if they are currently king
          if (player1Role === "king") {
            setKingWins(k => k + 1);
            setWinner("king"); // P1 wins
          } else {
            setVillagerWins(v => v + 1);
            setWinner("villager"); // P2 wins (they are king)
          }
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [gameStarted, gameOver, player1Role]);

  // Prison timer
  useEffect(() => {
    if (!villagerInPrison) return;

    const timer = setInterval(() => {
      setPrisonTimer(prev => {
        if (prev <= 1) {
          setVillagerInPrison(false);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [villagerInPrison]);

  // Main game loop
  useEffect(() => {
    if (!gameStarted || gameOver) return;

    const gameLoop = () => {
      // Determine which key controls which role
      const villagerKey = player1Role === "villager" ? "Space" : "Enter";
      const kingKey = player1Role === "king" ? "Space" : "Enter";

      const villagerPressing = keysPressed.current.has(villagerKey);
      const kingPressing = keysPressed.current.has(kingKey);

      // Update king looking state
      setIsKingLooking(kingPressing);

      // Deduct stamina chunk when king starts looking (once per press)
      if (kingPressing && !wasLookingRef.current) {
        staminaRef.current -= LOOK_STAMINA_COST;
        if (staminaRef.current <= 0) {
          staminaRef.current = 100;
          handleKill();
        }
        setKingStamina(staminaRef.current);
      }
      wasLookingRef.current = kingPressing;

      // Update villager charging (only if not in prison)
      let villagerKilled = false;

      if (!villagerInPrison) {
        setIsVillagerCharging(villagerPressing);

        if (villagerPressing) {
          chargeRef.current += CHARGE_SPEED;
          if (chargeRef.current >= 100) {
            // Villager kills king!
            villagerKilled = true;
            chargeRef.current = 0;
            handleKill();
          }
          setVillagerCharge(chargeRef.current);
        } else {
          // Reset charge when not pressing
          chargeRef.current = 0;
          setVillagerCharge(0);
        }
      } else {
        setIsVillagerCharging(false);
        chargeRef.current = 0;
        setVillagerCharge(0);
      }

      // Check if king catches villager (only if villager didn't just kill)
      if (kingPressing && villagerPressing && !villagerInPrison && !villagerKilled) {
        // Caught! Villager goes to prison
        setVillagerInPrison(true);
        setPrisonTimer(7);
        chargeRef.current = 0;
        setVillagerCharge(0);
        staminaRef.current = 100; // Full stamina on catch
        setKingStamina(100);
      }

      // Drain king stamina (only if villager is not in prison)
      if (!villagerInPrison) {
        staminaRef.current -= STAMINA_DRAIN;
        if (staminaRef.current <= 0) {
          // King dies from exhaustion - swap roles
          staminaRef.current = 100;
          handleKill();
        }
        setKingStamina(staminaRef.current);
      }

      // Walking animation
      setWalkPosition(prev => prev + WALK_SPEED);

      gameLoopRef.current = requestAnimationFrame(gameLoop);
    };

    gameLoopRef.current = requestAnimationFrame(gameLoop);

    return () => {
      if (gameLoopRef.current) {
        cancelAnimationFrame(gameLoopRef.current);
      }
    };
  }, [gameStarted, gameOver, player1Role, villagerInPrison]);

  const handleKill = () => {
    // Villager kills king - swap roles
    setPlayer1Role(prev => prev === "villager" ? "king" : "villager");
    staminaRef.current = 100;
    setKingStamina(100);
    chargeRef.current = 0;
    setVillagerCharge(0);
    setIsVillagerCharging(false);
    setIsKingLooking(false);
    setVillagerInPrison(false);
  };

  const startGame = () => {
    setGameStarted(true);
    setGameOver(false);
    setWinner(null);
    setTimeLeft(GAME_DURATION);
    staminaRef.current = 100;
    setKingStamina(100);
    chargeRef.current = 0;
    wasLookingRef.current = false;
    setVillagerCharge(0);
    setIsKingLooking(false);
    setIsVillagerCharging(false);
    setVillagerInPrison(false);
    setWalkPosition(0);
    setPlayer1Role("villager");
  };

  const restartGame = () => {
    startGame();
  };

  return (
    <div className="murder-container">
      <div className="murder-game-area">
        {/* King's stamina bar */}
        <div className="stamina-bar-container">
          <div className="stamina-label">King's Stamina</div>
          <div className="stamina-bar">
            <div
              className="stamina-fill"
              style={{ width: `${kingStamina}%` }}
            />
          </div>
        </div>

        {/* Villager's charge bar */}
        <div className="charge-bar-container">
          <div className="charge-label">Villager Attack</div>
          <div className="charge-bar">
            <div
              className={`charge-fill ${villagerCharge > 80 ? "critical" : ""}`}
              style={{ width: `${villagerCharge}%` }}
            />
          </div>
        </div>

        {/* Game scene */}
        <div className="murder-scene">
          {/* Background elements moving */}
          <div
            className="background-scroll"
            style={{ transform: `translateX(-${walkPosition % 800}px)` }}
          />

          {/* Villager (left) */}
          <div className={`character villager ${isVillagerCharging ? "charging" : ""} ${villagerInPrison ? "in-prison" : ""}`}>
            <div className="character-sprite villager-sprite">
              {villagerInPrison && <div className="prison-bars" />}
              <div className="villager-body"></div>
              <div className="villager-head"></div>
              {isVillagerCharging && <div className="weapon-arm"></div>}
            </div>
            <div className="role-label">
              {player1Role === "villager" ? "P1" : "P2"} - Villager
            </div>
          </div>

          {/* King (right) */}
          <div className={`character king ${isKingLooking ? "looking-back" : ""}`}>
            <div className="character-sprite king-sprite">
              <div className="king-body"></div>
              <div className="king-head">
                <div className="crown"></div>
              </div>
            </div>
            <div className="role-label">
              {player1Role === "king" ? "P1" : "P2"} - King
            </div>
          </div>
        </div>

        {/* Timer (for testing) */}
        <div className="timer-display">
          Time: {timeLeft}s
        </div>

        {/* Prison timer */}
        {villagerInPrison && (
          <div className="prison-timer">
            Prison: {prisonTimer}s
          </div>
        )}

        {/* Instructions */}
        {!gameStarted && !gameOver && (
          <div className="murder-overlay">
            <div className="murder-modal">
              <h2>Villager</h2>
              <div className="instructions">
                <p><strong>Player 1 (Villager):</strong> SPACE</p>
                <p><strong>Player 2 (King):</strong> ENTER</p>
                <hr />
                <p>Villager: Hold to charge weapon. Full = Kill!</p>
                <p>King: Press to look back. Catch the villager charging!</p>
                <p>King's stamina drains. If it hits 0, you die!</p>
              </div>
              <button onClick={startGame}>Start Game</button>
            </div>
          </div>
        )}

        {/* Game Over */}
        {gameOver && (
          <div className="murder-overlay">
            <div className="murder-modal">
              <h2>{winner === "king" ? "Player 1 Wins!" : "Player 2 Wins!"}</h2>
              <p>
                Time's up! {winner === "king" ? "P1" : "P2"} was king at the end.
              </p>
              <button onClick={restartGame}>Play Again</button>
            </div>
          </div>
        )}
      </div>

      <div className="murder-controls">
        <span>P1 (Villager): SPACE | P2 (King): ENTER</span>
      </div>
    </div>
  );
};

export default Villager;
